import type { SseEvent, AnswerSubmission } from '../types/models'

const BASE = import.meta.env.GCP_API_URL as string

export async function* streamAnswer(
  submission: AnswerSubmission,
  token: string,
  signal?: AbortSignal
): AsyncGenerator<SseEvent, void, unknown> {
  let response: Response
  try {
    response = await fetch(`${BASE}/quiz/conversation/answer/stream`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Accept': 'text/event-stream',
      },
      // This client renders a blank-feedback turn, which unlocks the backend's one-call IDK path.
      body: JSON.stringify({ ...submission, blankFeedbackOk: true }),
      signal,
    })
  } catch {
    yield { type: 'error', message: 'Network error during streaming' }
    return
  }

  if (!response.ok || !response.body) {
    yield { type: 'error', message: `HTTP ${response.status}` }
    return
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  let currentEvent: string | null = null
  const dataLines: string[] = []

  while (true) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })

    const lines = buffer.split('\n')
    buffer = lines.pop() ?? ''

    for (const line of lines) {
      if (line.startsWith('event:')) {
        currentEvent = line.slice(6).trim()
      } else if (line.startsWith('data:')) {
        dataLines.push(line.slice(5))
      } else if (line === '') {
        if (currentEvent && dataLines.length > 0) {
          const data = dataLines.join('\n')
          if (currentEvent === 'delta') {
            yield { type: 'delta', text: data }
          } else if (currentEvent === 'followup') {
            yield { type: 'followup', text: data }
          } else if (currentEvent === 'closing') {
            yield { type: 'closing', text: data }
          } else if (currentEvent === 'complete') {
            try { yield { type: 'complete', result: JSON.parse(data) } } catch { /* ignore */ }
          } else if (currentEvent === 'error') {
            yield { type: 'error', message: data }
          } else if (currentEvent === 'status') {
            try {
              const parsed = JSON.parse(data)
              if (parsed.type === 'retrying') {
                yield { type: 'retrying', waitMs: parsed.waitMs as number }
              }
            } catch { /* ignore malformed status */ }
          }
        }
        currentEvent = null
        dataLines.length = 0
      }
    }
  }
}
